import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Ramda from 'ramda';
import * as Yup from 'yup';

//MUI
import { Box, Button, DialogContent, Grid, Typography } from '@mui/material';

import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import type { Languages } from '../../../../../../../../../i18n/i18n.language-config';
import {
  ControlledLanguageCurrencyField,
  ControlledTextField,
  stripSpecialChars,
} from './shared/common';
import { Show } from '../../../../../../../../../components/Show';
import { CardContainer } from './shared/StyledCardContainer';
import { AddItemDialogActions } from './shared/AddItemDialogActions';

const CyberForensicProviderModal = ({
  data: modalProps,
  close,
}: ModalProps) => {
  const scrollableRef = React.useRef<null | HTMLDivElement>(null);
  const formMethods =
    useForm<SpecifiedCybersecurityOrForensicProviderFormValues>({
      defaultValues: {
        providers: modalProps.defaultValues,
      },
      resolver: yupResolver(schema),
    });

  const fieldArrayMethods = useFieldArray({
    control: formMethods.control,
    name: 'providers',
  });

  const handleAddProvider = ({ scroll }: { scroll: boolean }) => {
    fieldArrayMethods.append({
      name: '',
      rate: 0,
    });

    if (scroll) {
      setTimeout(
        () => scrollableRef.current?.scrollIntoView({ behavior: 'smooth' }),
        300
      );
    }
  };

  React.useEffect(() => {
    if (formMethods.getValues().providers[0]?.name) {
      return;
    }

    handleAddProvider({ scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          Ramda.compose(close, modalProps.onSubmit)
        )}
      >
        <DialogContent style={{ padding: '1rem 2.5rem' }}>
          <Show when={!fieldArrayMethods.fields.length}>
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="primary">
                No Providers
              </Typography>
            </Box>
          </Show>

          {fieldArrayMethods.fields.map((field, index) => {
            return (
              <CardContainer key={field.id} elevation={3}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name={`providers[${index}].name`}
                      label="Provider name"
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledLanguageCurrencyField
                      name={`providers[${index}].rate`}
                      label="Provider Rate"
                      language={modalProps.language}
                      required
                    />
                  </Grid>
                </Grid>

                <Button
                  className="contrast-text"
                  style={{ position: 'absolute', top: 5, right: 5 }}
                  onClick={() => fieldArrayMethods.remove(index)}
                >
                  Remove
                </Button>
              </CardContainer>
            );
          })}
          <div ref={scrollableRef} />
        </DialogContent>
        <AddItemDialogActions
          addLabel="Add Provider"
          onAdd={() => handleAddProvider({ scroll: true })}
          onCancel={close}
        />
      </form>
    </FormProvider>
  );
};

export default CyberForensicProviderModal;

//types
type ModalProps = {
  data: {
    defaultValues: CyberForensicProvider[];
    onSubmit: (
      formValues: SpecifiedCybersecurityOrForensicProviderFormValues
    ) => void;
    language: Languages;
  };
  close: () => void;
};

export type SpecifiedCybersecurityOrForensicProviderFormValues = {
  providers: CyberForensicProvider[];
};

export type CyberForensicProvider = {
  name: string;
  rate: number;
};

//validations
const schema = Yup.object().shape({
  providers: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().trim().label('Provider name').required(),
      rate: Yup.number().transform(stripSpecialChars),
    })
  ),
});

export const CyberForensicProviderModalConfig = {
  CyberForensicProviderModal: {
    component: CyberForensicProviderModal,
    config: {
      title: 'Specified Cybersecurity or Forensic Provider',
      fullWidth: true,
      maxWidth: 'md',
    },
  },
};
